import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../common/Card.jsx';
import { useTheme } from '../../contexts/ThemeContext';
import interventionService from '../../services/interventionService';
import clientService from '../../services/clientService';
import InterventionCreateModal from './Interventions/InterventionCreateModal';
import InterventionEditModal from './Interventions/InterventionEditModal';
import InterventionDeleteModal from './Interventions/InterventionDeleteModal';
import InterventionViewModal from './Interventions/InterventionViewModal';
import InterventionRapportModal from './Interventions/InterventionRapportModal';
import articleService from '../../services/articleService';
import Pagination from '../common/Pagination.jsx';
import ClientSelectDropdown from '../common/ClientSelectDropdown';

const Interventions = () => {
  const { isDarkMode } = useTheme();
  const [interventions, setInterventions] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    maxPage: 0
  });
  const [filters, setFilters] = useState({
    clientId: '',
    dateDebut: '',
    dateFin: '',
    searchTerms: '',
    cloture: '' // Add cloture filter
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [viewMode, setViewMode] = useState('cards');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // CRUD modal states
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  // Rapport modal state
  const [showRapport, setShowRapport] = useState(false);

  // Replace the scannerRef declaration to avoid duplicate declaration error
  const scannerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch clients for filter dropdown
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await clientService.getClients(1000, 1);
        if (res.success && Array.isArray(res.data.list)) setClients(res.data.list);
      } catch {}
    };
    fetchClients();
  }, []);

  // Fetch interventions
  const loadInterventions = async (pageSize = pagination.pageSize, page = pagination.page, filterParams = filters) => {
    setLoading(true);
    setError('');
    try {
      const res = await interventionService.getInterventions(pageSize, page, filterParams);
      if (res.success) {
        setInterventions(res.data.list || []);
        setPagination({
          page: res.data.page,
          pageSize: res.data.pageSize,
          total: res.data.total,
          maxPage: res.data.maxPage
        });
      } else {
        setError(res.message);
      }
    } catch {
      setError('Erreur lors du chargement des interventions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInterventions();
    // eslint-disable-next-line
  }, []);

  const { user } = useAuth();

  // Full-page loading indicator while API responds
  if (loading) {
    return (
      <div className="p-4 sm:p-6 min-h-screen flex items-start justify-center">
        <div className="w-full max-w-6xl">
          <div className={`rounded-2xl shadow-sm border p-8 flex flex-col items-center justify-center ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ minHeight: '60vh' }}>
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDarkMode ? 'border-primary-400' : 'border-primary-600'}`} />
            <div className={`mt-4 text-lg font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Chargement des interventions...</div>
            <div className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Veuillez patienter pendant la récupération des données.</div>
          </div>
        </div>
      </div>
    );
  }

  // Pagination handlers
  const handlePageChange = (newPage) => {
    loadInterventions(pagination.pageSize, newPage, filters);
  };
  const handlePageSizeChange = (newPageSize) => {
    loadInterventions(newPageSize, 1, filters);
  };

  // Filter handlers
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };
  const applyFilters = () => {
    setFiltersApplied(Object.values(filters).some(v => v));
    loadInterventions(pagination.pageSize, 1, filters);
    setShowFilters(false);
  };
  const clearFilters = () => {
    setFilters({ clientId: '', dateDebut: '', dateFin: '', searchTerms: '', cloture: '' });
    setFiltersApplied(false);
    loadInterventions(pagination.pageSize, 1, { clientId: '', dateDebut: '', dateFin: '', searchTerms: '', cloture: '' });
    setShowFilters(false);
  };

  const handleDownloadPdf = async (interventionId) => {
  try {
    const res = await articleService.downloadInterventionPdf(interventionId);
    if (res.success) {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Intervention_${interventionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } else {
      alert(res.message || "Erreur lors du téléchargement du PDF");
    }
  } catch (err) {
    alert("Erreur lors du téléchargement du PDF");
  }
};

  // Pagination UI
  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.maxPage, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`min-w-[44px] flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            pagination.page === i
              ? isDarkMode
                ? 'bg-primary-700 text-white'
                : 'bg-primary-600 text-white'
              : isDarkMode
                ? 'text-gray-200 hover:bg-gray-700'
                : 'text-gray-700 hover:bg-gray-100'
          }`}
          aria-current={pagination.page === i ? 'page' : undefined}
        >
          {i}
        </button>
      );
    }

  // Sticky responsive pagination bar (keeps it visible & clickable on small screens)
    const startItem = ((pagination.page - 1) * pagination.pageSize) + 1;
    const endItem = Math.min(pagination.page * pagination.pageSize, pagination.total);

    // Build a compact page button set for mobile
    const mobilePages = [];
    for (let i = startPage; i <= endPage; i++) {
      mobilePages.push(
        <button
          key={`m-${i}`}
          onClick={() => handlePageChange(i)}
          className={`flex-shrink-0 px-2 py-1 rounded-md text-sm font-medium transition-colors ${
            pagination.page === i
              ? isDarkMode
                ? 'bg-primary-700 text-white'
                : 'bg-primary-600 text-white'
              : isDarkMode
                ? 'text-gray-200 hover:bg-gray-700'
                : 'text-gray-700 hover:bg-gray-100'
          }`}
          aria-current={pagination.page === i ? 'page' : undefined}
        >
          {i}
        </button>
      );
    }

    // For mobile, use shared Pagination component (simple previous/next) to keep consistent UI
    if (isMobile) {
      return (
        <div className={`sticky bottom-0 z-40 ${isDarkMode ? 'bg-gray-800 border-t border-gray-700' : 'bg-white border-t border-gray-200'} px-3 py-2`}>
          <div className="max-w-6xl mx-auto">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.maxPage || 1}
              pageSize={pagination.pageSize}
              totalItems={pagination.total}
              onPageChange={(p) => handlePageChange(p)}
              onPageSizeChange={(s) => handlePageSizeChange(s)}
            />
          </div>
        </div>
      );
    }

    return (
      <div className={`sticky bottom-0 z-40 ${isDarkMode ? 'bg-gray-800 border-t border-gray-700' : 'bg-white border-t border-gray-200'} px-4 py-3`}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className={`flex items-center text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            <span className="truncate">
              Affichage de {startItem} à {endItem} sur {pagination.total}
            </span>
            <select
              value={pagination.pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className={`ml-4 border rounded-md px-2 py-1 text-sm transition-colors ${
                isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'
              }`}
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
            </select>
          </div>

          <div className="flex items-center space-x-1 overflow-x-auto no-scrollbar">
            <button
              onClick={() => handlePageChange(1)}
              disabled={pagination.page === 1}
              className={`min-w-[44px] flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Première page"
            >
              <i className="fas fa-angle-double-left"></i>
            </button>
            <button
              onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
              disabled={pagination.page === 1}
              className={`min-w-[44px] flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Page précédente"
            >
              <i className="fas fa-angle-left"></i>
            </button>

            {pages}

            <button
              onClick={() => handlePageChange(Math.min(pagination.maxPage, pagination.page + 1))}
              disabled={pagination.page === pagination.maxPage}
              className={`min-w-[44px] flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Page suivante"
            >
              <i className="fas fa-angle-right"></i>
            </button>
            <button
              onClick={() => handlePageChange(pagination.maxPage)}
              disabled={pagination.page === pagination.maxPage}
              className={`min-w-[44px] flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Dernière page"
            >
              <i className="fas fa-angle-double-right"></i>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Card for one intervention - Made responsive
  
  const InterventionCard = ({ interv }) => {
    const handleRapport = () => {
      setSelectedIntervention(interv);
      setShowRapport(true);
    };
    const handleEntretien = () => {
      // Only allow entretien for non-closed interventions
      if (interv.cloture) {
        alert('Impossible d\'accéder à l\'entretien : cette intervention est clôturée.');
        return;
      }
      localStorage.setItem('entretienIntervention', JSON.stringify(interv));
      window.location.href = `/entretien?id=${interv.id}`;
    };
    return (
      <div className={`rounded-2xl border p-4 sm:p-5 mb-2 transition-all duration-200 hover:shadow-lg group ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
          <div className="flex items-center space-x-2 mb-2 sm:mb-0">
            {interv.numeroIntervention && (
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${isDarkMode ? 'bg-primary-900 text-primary-300 border border-primary-700' : 'bg-primary-100 text-primary-800'}`}>
                <i className="fas fa-hashtag mr-1"></i>
                {interv.numeroIntervention}
              </span>
            )}
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              interv.cloture
                ? (isDarkMode ? 'bg-green-900 text-green-300 border border-green-700' : 'bg-green-100 text-green-800')
                : (isDarkMode ? 'bg-gray-700 text-gray-200 border border-gray-600' : 'bg-gray-100 text-gray-800')
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                interv.cloture ? (isDarkMode ? 'bg-green-400' : 'bg-green-400') : (isDarkMode ? 'bg-gray-400' : 'bg-gray-400')
              }`}></span>
              {interv.cloture ? 'Clôturée' : 'En cours'}
            </span>
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {interv.typeIntervention === 1 ? 'Préventive' : interv.typeIntervention === 2 ? 'Curative' : '-'}
            </span>
          </div>
          {/* Action buttons - Made responsive */}
          <div className="flex flex-wrap gap-1 sm:gap-2">
            <button
              className={`${isDarkMode ? 'text-primary-400 hover:text-white hover:bg-gray-700' : 'text-primary-600 hover:text-primary-900 hover:bg-primary-50'} p-2 rounded-lg transition-colors text-sm`}
              title="Voir"
              onClick={() => {
                setSelectedIntervention(interv);
                setShowView(true);
              }}
            >
              <i className="fas fa-eye"></i>
            </button>
            <button
              className={`${isDarkMode ? 'text-blue-400 hover:text-white hover:bg-blue-700' : 'text-blue-600 hover:text-blue-900 hover:bg-blue-50'} p-2 rounded-lg transition-colors text-sm`}
              title="Rapport"
              onClick={handleRapport}
            >
              <i className="fas fa-file-alt"></i>
            </button>
            {user?.isAdmin && (
              <>
                <button
                  className={`${isDarkMode ? 'text-primary-400 hover:text-white hover:bg-gray-700' : 'text-primary-600 hover:text-primary-900 hover:bg-primary-50'} p-2 rounded-lg transition-colors text-sm`}
                  title="Modifier"
                  onClick={() => { setSelectedIntervention(interv); setShowEdit(true); }}
                >
                  <i className="fas fa-edit"></i>
                </button>
                <button
                  className={`${isDarkMode ? 'text-red-400 hover:text-white hover:bg-gray-700' : 'text-red-600 hover:text-red-900 hover:bg-red-50'} p-2 rounded-lg transition-colors text-sm`}
                  title="Supprimer"
                  onClick={() => { setSelectedIntervention(interv); setShowDelete(true); }}
                >
                  <i className="fas fa-trash"></i>
                </button>
                <button
                  className={`${
                    interv.cloture 
                      ? (isDarkMode ? 'text-gray-500 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed')
                      : (isDarkMode ? 'text-primary-400 hover:text-white hover:bg-primary-700' : 'text-primary-600 hover:text-primary-900 hover:bg-primary-50')
                  } p-2 rounded-lg transition-colors text-sm`}
                  title={interv.cloture ? "Intervention clôturée - Entretien non disponible" : "Entretien"}
                  onClick={interv.cloture ? undefined : handleEntretien}
                  disabled={interv.cloture}
                >
                  <i className="fas fa-tools"></i>
                </button>
              </>
            )}
            {(user?.isAdmin || interv.cloture) && (
              <button
                className={`${isDarkMode ? 'text-green-400 hover:text-white hover:bg-green-700' : 'text-green-600 hover:text-green-900 hover:bg-green-50'} p-2 rounded-lg transition-colors text-sm`}
                title="Télécharger le PDF"
                onClick={() => handleDownloadPdf(interv.id)}
              >
                <i className="fas fa-file-download"></i>
              </button>
            )}
          </div>
        </div>
        {/* Date section - Made responsive */}
        <div className="mb-3">
          <div className="flex flex-col sm:flex-row sm:items-center text-sm space-y-1 sm:space-y-0">
            <div className="flex items-center">
              <i className="fas fa-calendar-alt mr-2 text-primary-500"></i>
              <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                {interv.startDate ? new Date(interv.startDate).toLocaleString('fr-FR') : '-'}
              </span>
            </div>
            <span className="hidden sm:inline mx-2 text-gray-400">→</span>
            <span className="sm:hidden text-gray-400 text-xs">à</span>
            <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
              {interv.endDate ? new Date(interv.endDate).toLocaleString('fr-FR') : '-'}
            </span>
          </div>
        </div>
        {/* Client section - Made responsive */}
        <div className="mb-3">
          <div className="flex flex-col sm:flex-row sm:items-center text-sm space-y-1 sm:space-y-0">
            <div className="flex items-center">
              <i className="fas fa-user mr-2 text-primary-500"></i>
              <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                {interv.client?.raisonSocial || '-'}
              </span>
            </div>
            {interv.client?.matriculeFiscal && (
              <>
                <span className="hidden sm:inline mx-2 text-gray-400">|</span>
                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-xs sm:text-sm`}>
                  {interv.client?.matriculeFiscal}
                </span>
              </>
            )}
          </div>
        </div>
        {/* Local section - Made responsive */}
        <div className="mb-3">
          <div className="flex flex-col sm:flex-row sm:items-center text-sm space-y-1 sm:space-y-0">
            <div className="flex items-center">
              <i className="fas fa-building mr-2 text-primary-500"></i>
              <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                {interv.local?.adresse || '-'}
              </span>
            </div>
            {interv.local?.responsable && (
              <>
                <span className="hidden sm:inline mx-2 text-gray-400">|</span>
                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-xs sm:text-sm`}>
                  {interv.local?.responsable}
                </span>
              </>
            )}
          </div>
        </div>
        {/* Observation section */}
        {interv.observation && (
          <div className={`text-xs sm:text-sm mt-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <i className="fas fa-sticky-note mr-1"></i>
            <span className="break-words">{interv.observation}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    // add bottom padding so sticky pagination won't overlap content
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6 pb-24">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className={`text-2xl sm:text-3xl font-bold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Interventions</h1>
          <p className={`text-sm sm:text-base mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Gestion des interventions et maintenances
            {filtersApplied && (
              <span className="text-primary-600 font-medium"> (Filtré)</span>
            )}
          </p>
        </div>
        {/* Desktop actions */}
        <div className="hidden lg:flex space-x-3 flex-shrink-0">
          <button
            onClick={() => loadInterventions()}
            disabled={loading}
            className={`${isDarkMode ? 'bg-gray-800 border border-gray-600 text-gray-200 hover:bg-gray-700' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'} px-4 py-2 rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <i className={`fas fa-sync-alt mr-2 ${loading ? 'animate-spin' : ''}`}></i>
            Actualiser
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`border px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
              filtersApplied
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : isDarkMode
                  ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="fas fa-filter mr-2"></i>
            Filtrer
            {filtersApplied && <span className="ml-1 bg-primary-500 text-white rounded-full px-1.5 py-0.5 text-xs">•</span>}
          </button>
          {user?.isAdmin && (
            <button
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-all text-sm font-medium"
              onClick={() => setShowCreate(true)}
            >
              <i className="fas fa-plus mr-2"></i>
              Nouvelle Intervention
            </button>
          )}
        </div>
        {/* Mobile actions */}
        <div className="flex flex-col gap-2 mt-2 lg:hidden">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`border px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center justify-center ${
              filtersApplied
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : isDarkMode
                  ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="fas fa-filter mr-2"></i>
            Filtrer
            {filtersApplied && <span className="ml-1 bg-primary-500 text-white rounded-full px-1.5 py-0.5 text-xs">•</span>}
          </button>
          {user?.isAdmin && (
            <button
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-all text-sm font-medium flex items-center justify-center"
              onClick={() => setShowCreate(true)}
            >
              <i className="fas fa-plus mr-2"></i>
              Nouvelle Intervention
            </button>
          )}
        </div>
      </div>

      {/* View Switcher - Made visible on mobile */}
      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-sm border p-4`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            {pagination.total} intervention(s) • Page {pagination.page} sur {pagination.maxPage}
          </span>
          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} flex items-center rounded-lg p-1 w-full sm:w-auto`}>
            <button
              onClick={() => setViewMode('cards')}
              className={`flex-1 sm:flex-none px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                viewMode === 'cards'
                  ? `${isDarkMode ? 'bg-gray-800 text-primary-400 shadow-sm' : 'bg-white text-primary-600 shadow-sm'}`
                  : `${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`
              }`}
            >
              <i className="fas fa-th-large mr-1"></i>
              Cartes
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex-1 sm:flex-none px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                viewMode === 'table'
                  ? `${isDarkMode ? 'bg-gray-800 text-primary-400 shadow-sm' : 'bg-white text-primary-600 shadow-sm'}`
                  : `${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`
              }`}
            >
              <i className="fas fa-list mr-1"></i>
              Tableau
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`${isDarkMode ? 'bg-gray-900 border border-red-700 text-red-300' : 'bg-red-50 border border-red-200 text-red-700'} px-4 py-3 sm:px-6 sm:py-4 rounded-lg`}>
          <div className="flex items-start">
            <i className="fas fa-exclamation-circle mr-2 mt-0.5 flex-shrink-0"></i>
            <span className="text-sm sm:text-base">{error}</span>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <Card className={`${isDarkMode ? 'border-l-4 border-l-primary-700 bg-gray-800' : 'border-l-4 border-l-primary-500'}`}>
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
              <div className="flex items-center mb-2 sm:mb-0">
                <div className="p-2 bg-primary-100 rounded-lg mr-3">
                  <i className="fas fa-filter text-primary-600"></i>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Filtres de recherche</h3>
                  <p className="text-sm text-gray-600">Affinez votre recherche d'interventions</p>
                </div>
              </div>
              <button
                onClick={() => setShowFilters(false)}
                className="self-end sm:self-auto text-gray-400 hover:text-gray-600 p-1"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Search Terms */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-white">
                  <i className="fas fa-search mr-2 text-gray-400"></i>
                  Recherche générale
                </label>
                <input
                  type="text"
                  value={filters.searchTerms}
                  onChange={e => handleFilterChange('searchTerms', e.target.value)}
                  placeholder="Client, local, observation..."
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors text-base
                    ${isDarkMode
                      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-primary-700'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                    }`
                  }
                />
              </div>

              {/* Cloture Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-white">
                  <i className="fas fa-check-circle mr-2 text-gray-400"></i>
                  Statut de clôture
                </label>
                <select
                  value={filters.cloture}
                  onChange={e => handleFilterChange('cloture', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors text-base
                    ${isDarkMode
                      ? 'bg-gray-800 border-gray-600 text-white focus:ring-primary-700'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                    }`
                  }
                >
                  <option value="">Tous les statuts</option>
                  <option value="true">Clôturées</option>
                  <option value="false">En cours</option>
                </select>
              </div>

              {/* Client Dropdown */}
              {user?.isAdmin && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-white">
                    <i className="fas fa-user mr-2 text-gray-400"></i>
                    Client
                  </label>
                  <ClientSelectDropdown
                    value={filters.clientId}
                    onChange={val => handleFilterChange('clientId', val)}
                    options={clients}
                    loading={false}
                    disabled={clients.length === 0}
                    isDarkMode={isDarkMode}
                    placeholder="Tous les clients"
                    required={false}
                  />
                </div>
              )}

            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium order-2 sm:order-1"
              >
                <i className="fas fa-times mr-2"></i>
                Effacer
              </button>
              <button
                onClick={applyFilters}
                className="px-6 py-2 bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors text-sm font-medium order-1 sm:order-2"
              >
                <i className="fas fa-search mr-2"></i>
                Appliquer les filtres
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* Interventions List */}
      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-sm border overflow-hidden`}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isDarkMode ? 'border-primary-400' : 'border-primary-600'}`}></div>
            <span className={`ml-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Chargement des interventions...</span>
          </div>
        ) : interventions.length === 0 ? (
          <div className="text-center py-12">
            <div className={`${isDarkMode ? 'text-gray-600' : 'text-gray-400'} mb-4`}>
              <i className="fas fa-tools text-6xl"></i>
            </div>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Aucune intervention trouvée</h3>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              {filtersApplied
                ? 'Aucune intervention ne correspond à vos critères de recherche.'
                : 'Commencez par ajouter votre première intervention.'}
            </p>
            {user?.isAdmin && (
              <button
                className={`${isDarkMode ? 'bg-primary-700 text-white hover:bg-primary-800' : 'bg-primary-600 text-white hover:bg-primary-700'} px-4 py-2 rounded-lg transition-colors`}
                onClick={() => setShowCreate(true)}
              >
                <i className="fas fa-plus mr-2"></i>
                Nouvelle Intervention
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Cards View */}
            {viewMode === 'cards' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 p-3 sm:p-6">
                {interventions.map(interv => (
                  <InterventionCard key={interv.id} interv={interv} />
                ))}
              </div>
            )}

            {/* Table View - scrolls horizontally on small screens */}
            {viewMode === 'table' && (
              <div className="overflow-x-auto">
                <div>
                  <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>N°</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>Début</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>Fin</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>Client</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>Local</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>Clôturée</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>Type</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>Observation</th>
                        <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className={isDarkMode ? 'bg-gray-800 divide-y divide-gray-700' : 'bg-white divide-y divide-gray-200'}>
                      {interventions.map(interv => (
                        <tr key={interv.id} className={isDarkMode ? 'hover:bg-gray-700 transition-colors' : 'hover:bg-gray-50 transition-colors'}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-bold ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`}>
                              {interv.numeroIntervention || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {interv.startDate ? new Date(interv.startDate).toLocaleString('fr-FR') : '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {interv.endDate ? new Date(interv.endDate).toLocaleString('fr-FR') : '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {interv.client?.raisonSocial || '-'}
                            </div>
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {interv.client?.matriculeFiscal || ''}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {interv.local?.adresse || '-'}
                            </div>
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {interv.local?.responsable || ''}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              interv.cloture
                                ? (isDarkMode ? 'bg-green-900 text-green-300 border border-green-700' : 'bg-green-100 text-green-800')
                                : (isDarkMode ? 'bg-gray-700 text-gray-200 border border-gray-600' : 'bg-gray-100 text-gray-800')
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                                interv.cloture ? (isDarkMode ? 'bg-green-400' : 'bg-green-400') : (isDarkMode ? 'bg-gray-400' : 'bg-gray-400')
                              }`}></span>
                              {interv.cloture ? 'Oui' : 'Non'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {interv.typeIntervention === 1 ? 'Préventive' : interv.typeIntervention === 2 ? 'Curative' : '-'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {interv.observation || '-'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                className={`${isDarkMode ? 'text-primary-400 hover:text-white hover:bg-gray-700' : 'text-primary-600 hover:text-primary-900 hover:bg-primary-50'} p-2 rounded-lg transition-colors`}
                                title="Voir"
                                onClick={() => {
                                  setSelectedIntervention(interv);
                                  setShowView(true);
                                }}
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                              <button
                                className={`${isDarkMode ? 'text-blue-400 hover:text-white hover:bg-blue-700' : 'text-blue-600 hover:text-blue-900 hover:bg-blue-50'} p-2 rounded-lg transition-colors`}
                                title="Rapport"
                                onClick={() => { setSelectedIntervention(interv); setShowRapport(true); }}
                              >
                                <i className="fas fa-file-alt"></i>
                              </button>
                              {user?.isAdmin && (
                                <>
                                  <button
                                    className={`${isDarkMode ? 'text-primary-400 hover:text-white hover:bg-gray-700' : 'text-primary-600 hover:text-primary-900 hover:bg-primary-50'} p-2 rounded-lg transition-colors`}
                                    title="Modifier"
                                    onClick={() => { setSelectedIntervention(interv); setShowEdit(true); }}
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    className={`${isDarkMode ? 'text-red-400 hover:text-white hover:bg-gray-700' : 'text-red-600 hover:text-red-900 hover:bg-red-50'} p-2 rounded-lg transition-colors`}
                                    title="Supprimer"
                                    onClick={() => { setSelectedIntervention(interv); setShowDelete(true); }}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                  <button
                                    className={`${
                                      interv.cloture 
                                        ? (isDarkMode ? 'text-gray-500 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed')
                                        : (isDarkMode ? 'text-primary-400 hover:text-white hover:bg-primary-700' : 'text-primary-600 hover:text-primary-900 hover:bg-primary-50')
                                    } p-2 rounded-lg transition-colors`}
                                    title={interv.cloture ? "Intervention clôturée - Entretien non disponible" : "Entretien"}
                                    onClick={interv.cloture ? undefined : () => {
                                      localStorage.setItem('entretienIntervention', JSON.stringify(interv));
                                      window.location.href = `/entretien?id=${interv.id}`;
                                    }}
                                    disabled={interv.cloture}
                                  >
                                    <i className="fas fa-tools"></i>
                                  </button>
                                </>
                              )}
                              {(user?.isAdmin || interv.cloture) && (
                                <button
                                  className={`${isDarkMode ? 'text-green-400 hover:text-white hover:bg-green-700' : 'text-green-600 hover:text-green-900 hover:bg-green-50'} p-2 rounded-lg transition-colors`}
                                  title="Télécharger le PDF"
                                  onClick={() => handleDownloadPdf(interv.id)}
                                >
                                  <i className="fas fa-file-download"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pagination */}
            {pagination.maxPage > 1 && renderPagination()}
          </>
        )}
      </div>

      {/* CRUD Modals */}
      {user?.isAdmin && (
        <>
          <InterventionCreateModal
            isOpen={showCreate}
            onClose={() => setShowCreate(false)}
            onSuccess={() => loadInterventions()}
          />
          <InterventionEditModal
            isOpen={showEdit}
            interventionId={selectedIntervention?.id}
            onClose={() => setShowEdit(false)}
            onSuccess={() => loadInterventions()}
          />
          <InterventionDeleteModal
            isOpen={showDelete}
            intervention={selectedIntervention}
            onClose={() => setShowDelete(false)}
            onSuccess={() => loadInterventions()}
          />
        </>
      )}
      <InterventionViewModal
        isOpen={showView}
        interventionId={selectedIntervention?.id}
        onClose={() => setShowView(false)}
      />
      {/* Rapport Modal */}
      <InterventionRapportModal
        open={showRapport}
        interventionId={selectedIntervention?.id}
        onClose={() => setShowRapport(false)}
      />
    </div>
  );
};

export default Interventions;